import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './authentication.service';
import { IJwtTokenUser } from '../models/models';

type PosthogConfig = { apiKey: string; host: string };

export type PosthogEnvConfig = {
  posthogApiKey?: string;
  posthogHost?: string;
};

@Injectable({ providedIn: 'root' })
export class PostHogService {
  /** Blocks double posthog.init() before `loaded` fires */
  private initStarted = false;
  /** SDK ready — gates trackCustomEvent and logout reset */
  private ready = false;
  /** Keys only in environment.test.ts / environment.prod.ts */
  private readonly config: PosthogConfig | null;

  constructor(
    private ngZone: NgZone,
    private authService: AuthenticationService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      this.config = null;
      return;
    }

    this.config = readPosthogConfig();
    if (!this.config) {
      return;
    }

    this.authService.currentUser.subscribe((user) => {
      if (!user?._id) {
        if (this.ready) {
          posthog.reset();
        }
        return;
      }

      if (!this.initStarted) {
        this.initPostHog();
        return;
      }

      if (this.ready) {
        this.syncUser(user);
      }
    });
  }

  trackCustomEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.config || !this.ready || !this.authService.currentUser.value?._id) {
      return;
    }
    posthog.capture(eventName, properties);
  }

  private initPostHog(): void {
    if (this.initStarted) {
      return;
    }
    this.initStarted = true;
    this.ngZone.runOutsideAngular(() => {
      posthog.init(this.config!.apiKey, {
        api_host: this.config!.host,
        defaults: '2026-05-30',
        person_profiles: 'identified_only',
        loaded: () => {
          const user = this.authService.currentUser.value;
          if (user?._id) {
            this.syncUser(user);
          }
          this.ready = true;
        },
      });
    });
  }

  private syncUser(user: IJwtTokenUser): void {
    posthog.identify(user._id!, {
      user_id: user._id,
      user_type: user.userType ?? '',
    });
    posthog.register({
      user_id: user._id,
      user_type: user.userType ?? '',
    });
  }
}

export function readPosthogConfig(
  env: PosthogEnvConfig = environment as typeof environment & PosthogEnvConfig,
): PosthogConfig | null {
  const apiKey = env.posthogApiKey?.trim();
  const host = env.posthogHost?.trim();
  if (!apiKey || !host) {
    return null;
  }
  return { apiKey, host };
}
